import type {
  RorosActorDTO,
  RorosActorPersonaDTO,
  UpsertActorPersonaPayload,
  RorosDelegationDTO,
  RorosVisibilityOverrideDTO,
  RorosPersonaConsultationDTO,
  RorosReadinessAssessmentDTO,
  CreateActorPayload,
  UpdateActorPayload,
  ChangeAdoptionLevelPayload,
  CreateDelegationPayload,
  SetVisibilityOverridePayload,
  ConsultPersonaPayload,
  CouncilConsultPayload,
  CreateReadinessAssessmentPayload,
  RorosOrgModeratorDefaultsDTO,
} from '@/types/roros/actors';
import { fetchJson, mutate, qs } from './base-roros.service';

// ─── Actors ─────────────────────────────────────────────────────────────────

export const RorosActorService = {
  list: (params?: Record<string, string>) =>
    fetchJson<RorosActorDTO[]>(`/api/v1/actors${qs(params)}`),
  create: (payload: CreateActorPayload) =>
    mutate<RorosActorDTO>('/api/v1/actors', 'POST', payload),
  get: (id: string) =>
    fetchJson<RorosActorDTO>(`/api/v1/actors/${id}`),
  update: (id: string, payload: UpdateActorPayload) =>
    mutate<RorosActorDTO>(`/api/v1/actors/${id}`, 'PUT', payload),
  deactivate: (id: string) =>
    mutate(`/api/v1/actors/${id}/deactivate`, 'PUT'),
  reactivate: (id: string) =>
    mutate<RorosActorDTO>(`/api/v1/actors/${id}/reactivate`, 'PUT'),
  archive: (id: string) =>
    mutate<RorosActorDTO>(`/api/v1/actors/${id}/archive`, 'POST'),
  assignDomain: (id: string, payload: { domainId: string }) =>  // A1
    mutate(`/api/v1/actors/${id}/domains`, 'POST', payload),
  unassignDomain: (id: string, domainId: string) =>
    mutate(`/api/v1/actors/${id}/domains/${domainId}`, 'DELETE'),
  changeAdoptionLevel: (id: string, payload: ChangeAdoptionLevelPayload) =>
    mutate(`/api/v1/actors/${id}/adoption-level`, 'PUT', payload),
  consultPersona: (personaId: string, payload: ConsultPersonaPayload) =>
    mutate<RorosPersonaConsultationDTO>(`/api/v1/actors/${personaId}/consult`, 'POST', payload),
  listConsultations: (params?: Record<string, string>) =>
    fetchJson<RorosPersonaConsultationDTO[]>(`/api/v1/actors/consultations${qs(params)}`),
  getConsultation: (id: string) =>
    fetchJson<RorosPersonaConsultationDTO>(`/api/v1/actors/consultations/${id}`),
  consultCouncil: (payload: CouncilConsultPayload) =>
    mutate<RorosPersonaConsultationDTO>('/api/v1/actors/council', 'POST', payload),
  createReadinessAssessment: (actorId: string, payload: CreateReadinessAssessmentPayload) =>
    mutate<RorosReadinessAssessmentDTO>(`/api/v1/actors/${actorId}/readiness-assessments`, 'POST', payload),
  listReadinessAssessments: (actorId: string, params?: Record<string, string>) =>
    fetchJson<{ data: RorosReadinessAssessmentDTO[]; meta: any }>(`/api/v1/actors/${actorId}/readiness-assessments${qs(params)}`),
  /** Wave B: org moderator defaults — AC-MOD-2 */
  getOrgModeratorDefaults: () =>
    fetchJson<RorosOrgModeratorDefaultsDTO>('/api/v1/actors/org-moderator-defaults'),
  setOrgMeetingModerator: (actorId: string | null) =>
    mutate('/api/v1/actors/org-moderator-defaults/meeting', 'PUT', { actorId }),
  setOrgDecisionModerator: (actorId: string | null) =>
    mutate('/api/v1/actors/org-moderator-defaults/decision', 'PUT', { actorId }),

  /** F-Meeting-1 Wave 1: persona CRUD */
  getPersona: (actorId: string) =>
    fetchJson<RorosActorPersonaDTO>(`/api/v1/actors/${actorId}/persona`),
  upsertPersona: (actorId: string, payload: UpsertActorPersonaPayload) => {
    // Map camelCase frontend keys → snake_case server keys (server schema uses strict snake_case)
    const body: Record<string, unknown> = {};
    if (payload.voiceDescription !== undefined) body.voice_description = payload.voiceDescription;
    if (payload.styleDescription !== undefined) body.style_description = payload.styleDescription;
    if (payload.valuesPriorities !== undefined) body.values_priorities = payload.valuesPriorities;
    if (payload.decisionStyle !== undefined) body.decision_style = payload.decisionStyle;
    if (payload.domainStances !== undefined) body.domain_stances = payload.domainStances;
    if (payload.delegationEnvelope !== undefined) body.delegation_envelope = payload.delegationEnvelope;
    if (payload.primaryLanguage !== undefined) body.primary_language = payload.primaryLanguage;
    if (payload.languageOverrides !== undefined) body.language_overrides = payload.languageOverrides;
    if (payload.languageRules !== undefined) body.language_rules = payload.languageRules;
    if (payload.provider !== undefined) body.provider = payload.provider;
    if (payload.modelTier !== undefined) body.model_tier = payload.modelTier;
    if (payload.instructions !== undefined) body.instructions = payload.instructions;
    return mutate<RorosActorPersonaDTO>(`/api/v1/actors/${actorId}/persona`, 'PUT', body);
  },
};

// ─── Delegations ────────────────────────────────────────────────────────────

export const RorosDelegationService = {
  list: (params?: Record<string, string>) =>
    fetchJson<RorosDelegationDTO[]>(`/api/v1/delegations${qs(params)}`),
  create: (payload: CreateDelegationPayload) =>
    mutate<RorosDelegationDTO>('/api/v1/delegations', 'POST', payload),
  get: (id: string) =>
    fetchJson<RorosDelegationDTO>(`/api/v1/delegations/${id}`),
  revoke: (id: string) =>
    mutate<RorosDelegationDTO>(`/api/v1/delegations/${id}/revoke`, 'POST'),
};

// ─── Visibility ─────────────────────────────────────────────────────────────

export const RorosVisibilityService = {
  get: (entityType: string, entityId: string) =>
    fetchJson<RorosVisibilityOverrideDTO>(`/api/v1/visibility/${entityType}/${entityId}`),
  set: (entityType: string, entityId: string, payload: SetVisibilityOverridePayload) =>
    mutate<RorosVisibilityOverrideDTO>(`/api/v1/visibility/${entityType}/${entityId}`, 'PUT', payload),
  remove: (entityType: string, entityId: string) =>
    mutate(`/api/v1/visibility/${entityType}/${entityId}`, 'DELETE'),
};
